import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type {
  SellerProfileData,
  SellerReviewItem,
  SellerReviewsSummary,
} from "../interfaces/seller.interface";
import type { Product } from "../../products/interfaces/products.interface";
import {
  resolveSellerId,
  fetchSellerProfile,
  fetchSellerProducts,
  fetchSellerReviews,
} from "../thunks/seller.thunk";

/** Redux slice state for the seller profile feature. */
export interface SellerState {
  resolvedSellerId: string | null;
  profile: SellerProfileData | null;
  products: Product[];
  reviews: SellerReviewItem[];
  reviewsSummary: SellerReviewsSummary | null;
  isResolvingSlug: boolean;
  isLoadingProfile: boolean;
  isLoadingProducts: boolean;
  isLoadingReviews: boolean;
  error: string | null;
}

const initialState: SellerState = {
  resolvedSellerId: null,
  profile: null,
  products: [],
  reviews: [],
  reviewsSummary: null,
  isResolvingSlug: false,
  isLoadingProfile: false,
  isLoadingProducts: false,
  isLoadingReviews: false,
  error: null,
};

const sellerSlice = createSlice({
  name: "seller",
  initialState,
  reducers: {
    clearSeller: (state) => {
      state.resolvedSellerId = null;
      state.profile = null;
      state.products = [];
      state.reviews = [];
      state.reviewsSummary = null;
      state.error = null;
    },
    setResolvedSellerId: (state, action: PayloadAction<string | null>) => {
      state.resolvedSellerId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Resolve seller id (slug -> uuid)
      .addCase(resolveSellerId.pending, (state) => {
        state.isResolvingSlug = true;
        state.error = null;
      })
      .addCase(resolveSellerId.fulfilled, (state, action) => {
        state.isResolvingSlug = false;
        state.resolvedSellerId = action.payload ?? null;
        state.error = null;
      })
      .addCase(resolveSellerId.rejected, (state, action) => {
        state.isResolvingSlug = false;
        state.resolvedSellerId = null;
        state.error = (action.payload as string) ?? "Failed to resolve seller";
      })
      // Fetch seller profile
      .addCase(fetchSellerProfile.pending, (state) => {
        state.isLoadingProfile = true;
        state.error = null;
      })
      .addCase(fetchSellerProfile.fulfilled, (state, action) => {
        state.isLoadingProfile = false;
        state.profile = action.payload ?? null;
        state.error = null;
      })
      .addCase(fetchSellerProfile.rejected, (state, action) => {
        state.isLoadingProfile = false;
        state.error = (action.payload as string) ?? "Failed to load profile";
      })
      // Fetch seller products
      .addCase(fetchSellerProducts.pending, (state) => {
        state.isLoadingProducts = true;
      })
      .addCase(fetchSellerProducts.fulfilled, (state, action) => {
        state.isLoadingProducts = false;
        state.products = action.payload ?? [];
      })
      .addCase(fetchSellerProducts.rejected, (state) => {
        state.isLoadingProducts = false;
        state.products = [];
      })
      // Fetch seller reviews
      .addCase(fetchSellerReviews.pending, (state) => {
        state.isLoadingReviews = true;
      })
      .addCase(fetchSellerReviews.fulfilled, (state, action) => {
        state.isLoadingReviews = false;
        if (action.payload) {
          state.reviews = action.payload.reviews ?? [];
          state.reviewsSummary = action.payload.summary ?? null;
        }
      })
      .addCase(fetchSellerReviews.rejected, (state) => {
        state.isLoadingReviews = false;
      });
  },
});

export const { clearSeller, setResolvedSellerId } = sellerSlice.actions;
export default sellerSlice.reducer;
